import { useMemo } from "react";
import { Run, RunMathFigures } from "@quantizr-engineering/common/types/run";
import { Show } from "@quantizr-engineering/common/types/show";
import {
  calculateRunFigures,
} from "@quantizr-engineering/common/math/live";

export const useRunDetailsCalculations = (run: Run, shows: Show[]) => {
  const runValues = useMemo<RunMathFigures>(() => {
    if (!run) {
      return {
        walkoutForecast: 0,
        walkoutActual: 0,
        walkoutPotential: 0,
        walkoutProjected: 0,
        artistTakeHomeForecast: 0,
        artistTakeHomeActual: 0,
        artistTakeHomePotential: 0,
        artistTakeHomeProjected: 0,
        commissionsForecast: [],
        commissionsActual: [],
        commissionsPotential: [],
        commissionsProjected: [],
        artistExpensesProjected: 0,
        artistExpensesActual: 0,
        artistExpensesForecast: 0,
        marginProjected: 0,
        marginActual: 0,
        marginForecast: 0,
        showBuyouts: 0,
        showBuyoutsProjected: 0,
        showBuyoutsPotential: 0,
      };
    }
    return calculateRunFigures(run, shows);
  }, [run, shows]);

  const {
    walkoutForecast,
    walkoutActual,
    artistTakeHomeForecast,
    artistTakeHomeActual: artistTakeHome,
    commissionsForecast,
    commissionsActual,
    artistExpensesProjected,
    artistExpensesActual,
    showBuyouts,
  } = runValues;

  const commissionsForecastTotal = commissionsForecast.reduce(
    (total, comm) => total + comm.amount,
    0
  );

  const commissionsActualTotal = commissionsActual.reduce(
    (total, comm) => total + comm.amount,
    0
  );

  const calculateDifference = (actual: number, estimated: number) => {
    return estimated ? ((actual - estimated) / estimated) * 100 : 0;
  };

  const runDetailsAccordions = useMemo(() => {
    return () => [
      {
        headerTitle: "Artist Take-Home",
        estimated: () => artistTakeHomeForecast,
        estimatedTitle: "Forecast",
        actual: () => artistTakeHome,
        difference: () =>
          calculateDifference(artistTakeHome, artistTakeHomeForecast),
        calculatedData: [
          {
            subSectionTitle: "Show Walkouts",
            estimated: runValues.walkoutForecast,
            actual: runValues.walkoutActual,
            difference: calculateDifference(
              runValues.walkoutActual,
              runValues.walkoutForecast
            ),
          },
          {
            subSectionTitle: "Total Paid Commissions",
            invertColors: true,
            estimated: commissionsForecastTotal,
            actual: commissionsActualTotal,
            difference: calculateDifference(
              commissionsActualTotal,
              commissionsForecastTotal
            ),
          },
          {
            subSectionTitle: "Run Expenses",
            invertColors: true,
            estimated: artistExpensesProjected,
            actual: artistExpensesActual,
            difference: calculateDifference(
              artistExpensesActual,
              artistExpensesProjected
            ),
          },
        ],
      },
      {
        headerTitle: "Paid Commissions",
        estimatedTitle: "Forecast",
        estimated: commissionsForecastTotal,
        actual: commissionsActualTotal,
        difference: calculateDifference(
          commissionsActualTotal,
          commissionsForecastTotal
        ),
        calculatedData: commissionsActual.map((commission) => ({
          subSectionTitle: commission.name,
          estimated:
            commissionsForecast.find(
              (forecast) => forecast.name === commission.name
            )?.amount || 0,
          actual: commission.amount,
          difference: 0,
        })),
      },
    ];
  }, [runValues, run]);

  return {
    runValues,
    runDetailsAccordions,
  };
};
